import { createFileRoute } from "@tanstack/react-router";
import { ServiceDetail } from "@/components/ServiceDetail";
import { serviceFaqs } from "@/data/serviceFaqs";
import { serviceGraph, abs } from "@/lib/serviceSchema";
import theaterImage from "@/assets/Apex_Theater.webp.asset.json";

const image = theaterImage.url;
const bullets = [
  "5.1.2, 5.1.4, 7.1.4, and larger immersive speaker layouts",
  "In-ceiling, on-ceiling, and architectural height-speaker options",
  "Receiver, processor, amplifier, and channel-count planning",
  "Single- and multi-subwoofer placement with bass management",
  "Retrofit speaker wiring for finished rooms and basements",
  "Measured level, delay, crossover, and room-correction calibration",
];

export const Route = createFileRoute("/services/dolby-atmos-home-theater")({
  head: () => ({
    meta: [
      { title: "Dolby Atmos Home Theater Installation Denver | Apex AV" },
      { name: "description", content: "Dolby Atmos home theater installation in Denver. Expert speaker layouts, overhead channels, subwoofer integration, retrofit wiring, and room calibration." },
      { name: "robots", content: "index, follow" },
      { name: "keywords", content: "Dolby Atmos home theater Denver, Dolby Atmos installation Denver, Dolby Atmos home theater installation, surround sound installation Denver, Atmos speaker installation, home theater calibration Denver, 7.1.4 Dolby Atmos" },
      { property: "og:title", content: "Dolby Atmos Home Theater Installation — Apex AV Denver" },
      { property: "og:description", content: "Immersive speaker design, clean installation, bass integration, and precise room calibration for Denver home theaters." },
      { property: "og:image", content: abs(image) },
      { property: "og:image:alt", content: "Dolby Atmos home theater designed and installed by Apex AV in Denver" },
      { property: "og:url", content: "https://apexav.com/services/dolby-atmos-home-theater" },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Dolby Atmos Home Theater Installation — Apex AV Denver" },
      { name: "twitter:description", content: "Speaker layout, overhead-channel installation, bass integration, and room calibration across the Denver metro." },
      { name: "twitter:image", content: abs(image) },
    ],
    links: [
      { rel: "canonical", href: "https://apexav.com/services/dolby-atmos-home-theater" },
      { rel: "preload", as: "image", href: image, fetchPriority: "high" } as never,
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          serviceGraph({
            slug: "dolby-atmos-home-theater",
            name: "Dolby Atmos Home Theater Installation",
            alternateName: ["Dolby Atmos Installation Denver", "Immersive Surround Sound Installation"],
            description: "Dolby Atmos home theater design, speaker installation, bass integration, and room calibration throughout Denver and the Front Range.",
            pageTitle: "Dolby Atmos Home Theater Installation Denver | Apex AV",
            pageDescription: "Dolby Atmos home theater installation in Denver with engineered speaker layouts, overhead channels, subwoofer integration, and room calibration.",
            image,
            faqs: serviceFaqs["dolby-atmos-home-theater"],
            serviceType: "Dolby Atmos Home Theater Installation",
            breadcrumbName: "Dolby Atmos Home Theater",
            offerCatalog: bullets,
            extraNodes: [
              {
                "@type": "VideoObject",
                name: "Dolby Atmos Home Theater Installation — Apex AV Denver",
                description: "Immersive home theater speaker design, installation, and calibration in Denver.",
                thumbnailUrl: ["https://apexav.com/videos/hero-theater.jpg"],
                uploadDate: "2026-06-01T00:00:00-06:00",
                contentUrl: "https://apexav.com/videos/home-theater-video.mp4",
                embedUrl: "https://apexav.com/services/dolby-atmos-home-theater",
              },
            ],
          }),
        ),
      },
    ],
  }),
  component: () => (
    <ServiceDetail
      faqs={serviceFaqs["dolby-atmos-home-theater"]}
      topics={["dolby-atmos", "home-theater", "prewire", "whole-home-audio"]}
      eyebrow="Dolby Atmos home theater"
      title="Hear the room disappear."
      intro={<>Professional <strong>Dolby Atmos home theater installation in Denver</strong>, designed around your seats and room geometry. We place every surround and overhead channel intentionally, integrate the subwoofers, and calibrate the finished system so sound moves naturally around and above you.</>}
      image={image}
      imageAlt="Immersive Dolby Atmos home theater installation in a Denver residence"
      video="/videos/home-theater-video.mp4"
      brands={["marantz", "denon", "klipsch", "episode", "sony", "sanus"]}
      bullets={bullets}
      bulletsHeading="What's included with Dolby Atmos installation"
      features={[
        { title: "Designed from the seats", desc: "Speaker angles and coverage begin at the listening positions, not at convenient ceiling cutouts." },
        { title: "Cleanly integrated", desc: "Wiring, architectural speakers, equipment, and acoustic details are coordinated with the finished room." },
        { title: "Measured and calibrated", desc: "Levels, delays, crossovers, bass management, and room correction are tuned for consistent performance." },
      ]}
      relatedServices={[
        { to: "/services/home-theater", label: "Custom Home Theater", desc: "Plan the complete room, including projection, seating, acoustics, lighting, and control." },
        { to: "/services/prewire", label: "New Construction Prewire", desc: "Reserve every speaker, subwoofer, rack, and conduit path before drywall." },
        { to: "/services/retrofit", label: "Retrofit & Cable Fishing", desc: "Add immersive audio to a finished room with carefully planned access." },
        { to: "/services/whole-home-audio", label: "Whole-Home Audio", desc: "Extend music beyond the theater with independent audio zones." },
      ]}
    />
  ),
});